import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api/client';
import type { TabUpdateSettings } from '@/lib/api/custom-dashboard';
import { saveCustomDashboardTabUpdateSettings } from '@/lib/api/custom-dashboard-update-settings';
import { allTabsQueryKey } from './allTabsQueryKey';
import { WEEKDAYS } from './updateSettingsFormat';

export interface UpdateSettingsDialogProps {
  accountId: string;
  authHeaders: Record<string, string>;
  tabId: string;
  tabName: string;
  /** 現在の設定（設定の無いタブは null）。 */
  current: TabUpdateSettings | null;
  /** 公開中の revision に details.txt があるか（無ければ定期更新は失敗になる）。 */
  hasDetails: boolean;
  onClose: () => void;
}

/**
 * 曜日の選び方の注意。定期バッチはデータ基準日の翌日に動くため、更新したい曜日をそのまま選ぶと 1 日ずれる。
 * 取り違えやすいので、曜日を選ぶ直前に強い色で知らせる。
 */
function WeekdayBasisNotice() {
  return (
    <div
      role="note"
      aria-label="曜日の選び方の注意"
      className="space-y-2 rounded-lg bg-orange-600 px-4 py-3 text-white shadow-sm"
    >
      <p className="flex items-center gap-1.5 text-sm font-bold">
        <TriangleAlertIcon size={16} aria-hidden="true" />
        注意
      </p>
      <p className="text-[15px] font-bold leading-relaxed">
        選択する曜日は更新を行う曜日ではなく「データ基準日」の曜日です！
        <br />
        月曜日に更新したい場合は日曜日を選択してください！更新したい曜日の1つ前を選択！
      </p>
      <div className="rounded-md bg-white/15 px-3 py-2 text-sm font-semibold leading-relaxed">
        <p>例）</p>
        <p>月曜日に1回だけ → 日曜日だけ選択</p>
        <p>月曜日、金曜日の2回 → 日曜日、木曜日</p>
      </div>
    </div>
  );
}

/**
 * タブの定期更新（T-0059）の設定ダイアログ。データ連携の定期バッチの後、最終データ連携日がチェックした曜日なら、
 * このタブのデータ更新を Agent が行う。曜日を 1 つも選ばずに保存すると定期更新を止める。
 */
export function UpdateSettingsDialog({
  accountId,
  authHeaders,
  tabId,
  tabName,
  current,
  hasDetails,
  onClose,
}: UpdateSettingsDialogProps) {
  const queryClient = useQueryClient();
  const [weekdays, setWeekdays] = useState<number[]>(current?.weekdays ?? []);
  const [autoActivate, setAutoActivate] = useState(current?.autoActivate ?? false);
  const [isSaving, setIsSaving] = useState(false);
  const hasWeekdays = weekdays.length > 0;

  const toggleWeekday = (value: number, checked: boolean) =>
    setWeekdays((prev) =>
      checked ? [...prev, value].sort((a, b) => a - b) : prev.filter((day) => day !== value),
    );

  const invalidateAllTabs = () =>
    queryClient.invalidateQueries({ queryKey: allTabsQueryKey(accountId) });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // 曜日が無ければ設定ごと消える（自動公開も残さない）。
      await saveCustomDashboardTabUpdateSettings(
        accountId,
        tabId,
        { weekdays, autoActivate: hasWeekdays && autoActivate },
        authHeaders,
      );
      await invalidateAllTabs();
      toast.success(hasWeekdays ? '定期更新の設定を保存しました' : '定期更新を止めました');
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        await invalidateAllTabs();
        toast.error('タブが見つかりません。一覧を読み直しました');
        onClose();
      } else {
        toast.error(
          error instanceof Error ? error.message : '定期更新の設定を保存できませんでした',
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tabName} の定期更新</DialogTitle>
          <DialogDescription>
            データ連携の定期バッチの後、最終データ連携日がチェックした曜日なら、details.txt
            の手順に従って Agent がデータを更新し、新しい revision として履歴に追加します
          </DialogDescription>
        </DialogHeader>
        <WeekdayBasisNotice />
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">最終データ連携日の曜日</legend>
          <div className="flex flex-wrap gap-4">
            {WEEKDAYS.map((day) => (
              <div key={day.value} className="flex items-center gap-1.5">
                <Checkbox
                  id={`update-settings-weekday-${day.value}`}
                  checked={weekdays.includes(day.value)}
                  onCheckedChange={(checked) => toggleWeekday(day.value, checked === true)}
                />
                <Label htmlFor={`update-settings-weekday-${day.value}`}>{day.label}</Label>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            曜日を 1 つも選ばずに保存すると、定期更新を止めます
          </p>
        </fieldset>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Checkbox
              id="update-settings-auto-activate"
              checked={hasWeekdays && autoActivate}
              disabled={!hasWeekdays}
              onCheckedChange={(checked) => setAutoActivate(checked === true)}
            />
            <Label htmlFor="update-settings-auto-activate">完了後に自動公開</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            自動で公開すると、人が確認する前に閲覧画面（プロダクトで表示している場合はプロダクトの画面も）が新しいデータに変わります
          </p>
        </div>
        {!hasDetails && (
          <Alert variant="destructive">
            <AlertDescription>
              公開中の revision に details.txt が無いため、定期更新は失敗になります。details.txt
              を含めてアップロードし、公開してください
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            キャンセル
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSaving}>
            {isSaving ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
